import React, { useState, useEffect, useRef } from "react";
import { IoChevronDown } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { isValidPhoneNumber } from "libphonenumber-js";
import PhoneInput from "react-phone-number-input";
import Card from "components/card";
import InputField from "components/fields/InputField";
import { staffAPI } from "services/staffService";

const EditStaffForm = () => {
  const navigate = useNavigate();
  const { staffId } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    status: "active",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [openDropdown, setOpenDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Fetch staff details on mount
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await staffAPI.getStaffById(staffId);
        const staff = response?.data || response;
        setFormData((prev) => ({
          ...prev,
          name: staff.name || "",
          email: staff.email || "",
          phone: staff.phone || "",
          status: staff.status || "active",
        }));
      } catch (err) {
        const message = err?.response?.data?.message || "Failed to load staff details";
        showToast(message, "error");
        setTimeout(() => navigate("/admin/staff-management"), 2000);
      } finally {
        setFetching(false);
      }
    };

    fetchStaff();
  }, [staffId, navigate]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (!isValidPhoneNumber(formData.phone)) {
      newErrors.phone = "Invalid phone number";
    }

    // Password validation only if provided
    if (formData.password) {
      if (formData.password.length < 8) {
        newErrors.password = "Password must be at least 8 characters";
      } else if (!/(?=.*[a-z])/.test(formData.password)) {
        newErrors.password = "Password must contain lowercase letter";
      } else if (!/(?=.*[A-Z])/.test(formData.password)) {
        newErrors.password = "Password must contain uppercase letter";
      } else if (!/(?=.*\d)/.test(formData.password)) {
        newErrors.password = "Password must contain number";
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Please confirm new password";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: undefined }));
    }
  };

  const handlePhoneChange = (value) => {
    setFormData((prev) => ({ ...prev, phone: value }));
    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: undefined }));
    }
  };

  const handleStatusChange = (e) => {
    setFormData((prev) => ({ ...prev, status: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone,
        status: formData.status,
      };

      // Only include password if provided
      if (formData.password) {
        payload.password = formData.password;
      }

      await staffAPI.updateStaff(staffId, payload);
      showToast("Staff member updated successfully", "success");
      setTimeout(() => {
        navigate("/admin/staff-management");
      }, 1500);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Failed to update staff";
      showToast(message, "error");

      // Handle field-specific errors from backend
      if (err?.response?.data?.errors) {
        const fieldErrors = {};
        err.response.data.errors.forEach((errObj) => {
          if (errObj.path) {
            fieldErrors[errObj.path] = errObj.msg || "Invalid";
          }
        });
        setErrors(fieldErrors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/staff-management");
  };

  if (fetching) {
    return (
      <div className="mt-3 flex h-full items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          <p className="text-gray-600 dark:text-gray-400">Loading staff details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 flex h-full w-full flex-col gap-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Edit Staff Member</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Update staff information (leave password blank to keep current)
        </p>
      </div>

      {/* Toast */}
      {toast.show && (
        <div
          className={`rounded-xl p-4 ${
            toast.type === "success"
              ? "bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {toast.message}
        </div>
      )}

      {/* Form Card */}
      <Card extra="p-6">
        <form onSubmit={handleSubmit} className="grid gap-6 md:grid-cols-2">
          {/* Name */}
          <div className="md:col-span-2">
            <InputField
              label="Full Name"
              id="name"
              type="text"
              placeholder="Enter staff member's full name"
              value={formData.name}
              onChange={handleChange}
              state={errors.name ? "error" : ""}
            />
            {errors.name && (
              <p className="mt-2 text-xs text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Email (Read-only) */}
          <div>
            <InputField
              label="Email Address"
              id="email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={handleChange}
              state={errors.email ? "error" : ""}
              disabled
            />
            {errors.email && (
              <p className="mt-2 text-xs text-red-500">{errors.email}</p>
            )}
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Email cannot be changed</p>
          </div>

          {/* Phone (Read-only) */}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Phone Number
            </label>
            <PhoneInput
              international
              countryCallingCodeEditable={true}
              defaultCountry="IN"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder="Enter phone number"
              disabled
              className={`mt-2 flex h-12 w-full items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none opacity-50 ${
                errors.phone
                  ? "border-red-500 dark:border-red-400"
                  : "border-gray-200 dark:border-white/10"
              }`}
            />
            {errors.phone && (
              <p className="mt-2 text-xs text-red-500">{errors.phone}</p>
            )}
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Phone cannot be changed</p>
          </div>

          {/* Password */}
          <div>
            <InputField
              label="New Password (Optional)"
              id="password"
              type="password"
              placeholder="Leave blank to keep current password"
              value={formData.password}
              onChange={handleChange}
              state={errors.password ? "error" : ""}
            />
            {errors.password && (
              <p className="mt-2 text-xs text-red-500">{errors.password}</p>
            )}
            {formData.password && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Must have 8+ characters with uppercase, lowercase, and number
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <InputField
              label="Confirm New Password"
              id="confirmPassword"
              type="password"
              placeholder="Re-enter new password"
              value={formData.confirmPassword}
              onChange={handleChange}
              state={errors.confirmPassword ? "error" : ""}
              disabled={!formData.password}
            />
            {errors.confirmPassword && (
              <p className="mt-2 text-xs text-red-500">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="mb-2 block text-sm font-medium text-navy-700 dark:text-white">
              Status
            </label>
            <div ref={dropdownRef} className="relative mt-2 w-full">
              <button
                type="button"
                onClick={() => setOpenDropdown((s) => !s)}
                className={`flex h-12 w-full items-center justify-between rounded-xl border px-3 text-left text-sm ${errors.status
                  ? "border-red-500 text-red-500"
                  : "border-gray-200 text-gray-700 hover:border-gray-300 dark:border-white/10 dark:text-white dark:hover:border-white/20"
                  }`}
              >
                <span className="truncate">{formData.status === "active" ? "Active" : formData.status === "in-active" ? "Inactive" : "Select status"}</span>
                <IoChevronDown className={`h-4 w-4 ${errors.status ? "text-red-500" : "text-gray-500"}`} />
              </button>

              {openDropdown && (
                <div className="absolute top-full z-50 mt-2 w-full rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-600 dark:bg-gray-800">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, status: "active" }));
                      setOpenDropdown(false);
                    }}
                    className={`h-10 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${formData.status === "active" ? "bg-blue-50 text-blue-600" : "text-gray-700 dark:text-gray-200"}`}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, status: "in-active" }));
                      setOpenDropdown(false);
                    }}
                    className={`h-10 w-full rounded-md px-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${formData.status === "in-active" ? "bg-blue-50 text-blue-600" : "text-gray-700 dark:text-gray-200"}`}
                  >
                    Inactive
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 md:col-span-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={loading}
              className="flex-1 rounded-xl border border-gray-200 py-3 font-medium text-navy-700 transition-all hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-white dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-xl bg-brand-500 py-3 font-medium text-white transition-all hover:bg-brand-600 disabled:opacity-50 dark:bg-brand-400 dark:hover:bg-brand-500"
            >
              {loading ? "Updating..." : "Update Staff Member"}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default EditStaffForm;
